/**
 * Device Registry Page - Admin Portal
 * Register, QC, and manage all Trackotic devices from manufacture to client
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Upload, Eye, CheckCircle, XCircle, Clock, 
  Cpu, Wifi, Thermometer, Droplets, Weight, Battery,
  Radio, Cloud, RefreshCw, Loader2
} from 'lucide-react';

export default function DeviceRegistryPage() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [qcModalOpen, setQcModalOpen] = useState(false);
  const [fotaModalOpen, setFotaModalOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    qc_passed: 0,
    qc_pending: 0,
    qc_failed: 0,
    fota_in_progress: 0,
    ready_to_dispatch: 0
  });

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await controlApi.getRegistryDevices({ filter });
      setDevices(response.data);
    } catch (error) {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchStats();
    fetchDevices();
  }, [fetchDevices]);

  const fetchStats = async () => {
    try {
      const response = await controlApi.getDeviceRegistryStats();
      setStats(response.data);
    } catch (error) {
        // Silently handled
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'PRO': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'HUB': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'LITE': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getQCBadge = (status) => {
    switch (status) {
      case 'passed':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Passed</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">In progress</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Failed</Badge>;
      case 'awaiting':
        return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Awaiting QC</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_stock':
        return <Badge variant="outline" className="text-slate-500">In stock</Badge>;
      case 'qc_fota':
        return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">QC / FOTA</Badge>;
      case 'failed_qc':
        return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Failed QC</Badge>;
      case 'new':
        return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">New · unassigned</Badge>;
      case 'provisioned':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Provisioned</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleViewDevice = (device) => {
    setSelectedDevice(device);
    if (device.qc_checklist) {
      setQcModalOpen(true);
    } else if (device.fota_details) {
      setFotaModalOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Registry</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Register, QC, and manage all Trackotic devices from manufacture to client
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2">
            <Upload className="w-4 h-4" />
            Import CSV batch
          </Button>
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={() => setRegisterModalOpen(true)}>
            <Plus className="w-4 h-4" />
            Register devices
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">Total registered</p>
            <p className="text-3xl font-bold text-blue-600">{stats.total.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">QC passed · ready</p>
            <p className="text-3xl font-bold text-green-600">{stats.qc_passed.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">QC pending / FOTA</p>
            <p className="text-3xl font-bold text-amber-600">{stats.qc_pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">QC failed</p>
            <p className="text-3xl font-bold text-red-600">{stats.qc_failed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Pills */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">All devices</h2>
        <div className="flex items-center gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'qc_pending', label: 'QC pending' },
            { key: 'fota', label: 'FOTA in progress' },
            { key: 'failed', label: 'Failed' },
            { key: 'ready', label: 'Ready' }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                filter === f.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Devices Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Device ID</TableHead>
                <TableHead className="w-[80px]">Type</TableHead>
                <TableHead className="w-[120px]">IMEI / EUI</TableHead>
                <TableHead className="w-[100px]">Firmware</TableHead>
                <TableHead className="w-[100px]">QC Status</TableHead>
                <TableHead className="w-[150px]">FOTA Progress</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No devices found
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell className="font-mono text-sm">{device.device_uid}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(device.device_type)}`}>
                        {device.device_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">
                      {device.imei?.substring(0, 8)}…
                    </TableCell>
                    <TableCell className="text-sm">{device.firmware || '—'}</TableCell>
                    <TableCell>{getQCBadge(device.qc_status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={device.fota_progress || 0} 
                          className={`h-2 w-20 ${device.fota_progress < 100 && device.fota_progress > 0 ? 'bg-amber-100' : ''}`}
                        />
                        <span className="text-xs text-slate-500">{device.fota_progress || 0}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(device.provisioning_status)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700"
                        onClick={() => handleViewDevice(device)}
                      >
                        {device.qc_status === 'awaiting' ? 'Start QC' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* QC Checklist Modal */}
      <Dialog open={qcModalOpen} onOpenChange={setQcModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>QC Checklist — {selectedDevice?.device_id}</DialogTitle>
          </DialogHeader>
          {selectedDevice?.qc_checklist && (
            <div className="space-y-3">
              {[
                { key: 'gnss_lock', label: 'GNSS lock acquired', icon: Radio },
                { key: 'gsm_registered', label: 'GSM SIM registered (Airtel M2M)', icon: Wifi },
                { key: 'temp_sensor', label: 'Temperature sensor reading valid', icon: Thermometer },
                { key: 'humidity_sensor', label: 'Humidity sensor reading valid', icon: Droplets },
                { key: 'weight_sensor', label: 'Weight sensor calibrated', icon: Weight },
                { key: 'reed_switch', label: 'Bag event — reed switch', icon: Cpu },
                { key: 'magnetic_confirm', label: 'Bag event — magnetic confirm', icon: Cpu },
                { key: 'api_verified', label: 'API post to cloud verified', icon: Cloud },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <item.icon className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">{item.label}</span>
                  </div>
                  {selectedDevice.qc_checklist[item.key] ? (
                    <span className="text-green-600 font-medium text-sm">Pass</span>
                  ) : (
                    <span className="text-red-600 font-medium text-sm">Fail</span>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <Battery className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">Battery charge level</span>
                </div>
                <span className="text-green-600 font-medium text-sm">{selectedDevice.qc_checklist.battery_level}%</span>
              </div>
              <div className="flex items-center justify-between py-3 bg-green-50 dark:bg-green-900/20 rounded-lg px-3">
                <span className="font-medium">Overall QC result</span>
                <span className="text-green-600 font-bold">PASSED ✓</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FOTA Status Modal */}
      <Dialog open={fotaModalOpen} onOpenChange={setFotaModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>FOTA Status — {selectedDevice?.device_id}</DialogTitle>
          </DialogHeader>
          {selectedDevice?.fota_details && (
            <div className="space-y-4">
              {[
                { label: 'Current firmware', value: selectedDevice.fota_details.current_firmware },
                { label: 'Target firmware', value: `${selectedDevice.fota_details.target_firmware} — released ${selectedDevice.fota_details.release_date}` },
                { label: 'Update method', value: selectedDevice.fota_details.method },
                { label: 'Started', value: selectedDevice.fota_details.started },
                { label: 'Estimated completion', value: selectedDevice.fota_details.eta },
              ].map((item, i) => (
                <div key={`idx-${i}`} className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                  <span className="text-sm text-slate-500">{item.label}</span>
                  <span className="text-sm font-medium">{item.value}</span>
                </div>
              ))}
              <div>
                <span className="text-sm text-slate-500">Progress</span>
                <div className="flex items-center gap-3 mt-2">
                  <Progress value={selectedDevice.fota_progress} className="h-3 flex-1" />
                  <span className="text-sm font-medium">{selectedDevice.fota_progress}%</span>
                </div>
              </div>
              <div>
                <span className="text-sm text-slate-500">What {selectedDevice.fota_details.target_firmware} includes</span>
                <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 leading-relaxed">
                  {selectedDevice.fota_details.changelog}
                </p>
              </div>
              <div className="flex justify-between py-2 border-t border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-500">Post-update action</span>
                <span className="text-sm font-medium">{selectedDevice.fota_details.post_action}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Register Device Modal */}
      <Dialog open={registerModalOpen} onOpenChange={setRegisterModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Register New Device</DialogTitle>
            <DialogDescription>Enter device details to register a new Trackotic device</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Device Type</label>
              <select className="w-full mt-1 p-2 border rounded-lg dark:bg-slate-800 dark:border-slate-700">
                <option value="PRO">PRO</option>
                <option value="HUB">HUB</option>
                <option value="LITE">LITE</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">IMEI / EUI</label>
              <Input placeholder="Enter IMEI or EUI number" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Firmware Version</label>
              <Input placeholder="e.g., v2.4.1" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterModalOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700">Register Device</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
